import { Auth0Provider } from "@auth0/auth0-react"
import { AuthProvider as OidcProvider } from "react-oidc-context"
import { WebStorageStateStore } from "oidc-client-ts"
import { useMemo } from "react"

import { isKeycloak, authIssuer, authClientId, authAudience, authRedirectUri } from "./runtime-config"
import { isEmbedded } from "../../data-utils/authUtils.js"

/**
 * Mounts whichever IdP SDK this environment is configured for.
 *
 * Both branches are configured to produce the SAME token contract:
 *   - audience `hasura` (Auth0 API identifier / Keycloak audience mapper), so the
 *     Hasura entry, the Node verifier and the Python verifier all accept it;
 *   - authorization code + PKCE, never implicit;
 *   - tokens in memory by default, localStorage only in the embedded iframe case.
 */

const AUTH0_ERROR_MESSAGES = {
  not_authorized: "Your account has not been granted access to this application. Contact your ChaosTrack administrator.",
  no_org: "Your account is not associated with an organization. Contact your ChaosTrack administrator.",
  server_error: "An error occurred during login. Please try again or contact your ChaosTrack administrator.",
}

export { AUTH0_ERROR_MESSAGES }

/**
 * Keycloak branch.
 *
 * `onSigninCallback` strips the OAuth response params (`code`, `state`) from the URL
 * after the exchange. Without it a refresh would replay a spent authorization code
 * and surface a confusing "invalid_grant" to the user.
 */
export const KeycloakAuthProvider = ({ children, onRedirect }) => {
  const embedded = isEmbedded()

  const oidcConfig = useMemo(
    () => ({
      authority: authIssuer(),
      client_id: authClientId(),
      redirect_uri: authRedirectUri(),
      post_logout_redirect_uri: `${window.location.origin}${process.env.PUBLIC_URL ?? "/"}`,
      response_type: "code",
      // `offline_access` makes Keycloak issue a REFRESH TOKEN. Without it, renewal falls
      // back to a hidden-iframe silent auth that depends on the Keycloak SSO session
      // cookie (ssoSessionIdleTimeout, 30 min) — so a user idle past that, or with the
      // iframe blocked, is logged out with no way to recover, even on reload. With a
      // refresh token, oidc-client-ts renews via the refresh_token grant (a network POST,
      // no iframe, no third-party cookie), which survives access-token expiry. This is the
      // Keycloak equivalent of the Auth0 SDK's refresh-token renewal. The public-SPA risk
      // is covered by refresh-token ROTATION, enabled on the realm in bootstrap.mjs.
      scope: "openid profile email offline_access",
      // Keycloak puts the custom audience on the access token via a protocol mapper;
      // asking for it as a resource keeps parity with the Auth0 `audience` param for
      // deployments that also enable audience validation on the authorization request.
      extraQueryParams: { audience: authAudience() },
      // Silent renew via a hidden iframe. In the embedded (cross-origin iframe) case
      // that cannot work, so refresh tokens carry the session instead — mirroring the
      // existing Auth0 `useRefreshTokens` switch.
      automaticSilentRenew: !embedded,
      ...(embedded ? { userStore: new WebStorageStateStore({ store: window.localStorage }) } : {}),
      onSigninCallback: (user) => {
        window.history.replaceState({}, document.title, window.location.pathname)
        onRedirect?.(user?.state)
      },
    }),
    [embedded, onRedirect]
  )

  if (!oidcConfig.authority || !oidcConfig.client_id) return null

  return <OidcProvider {...oidcConfig}>{children}</OidcProvider>
}

/** Auth0 branch — behaviour unchanged from the original provider. */
export const Auth0AuthProvider = ({ children, onRedirectCallback }) => {
  const embedded = isEmbedded()
  const domain = authIssuer().replace(/^https?:\/\//, "")
  const clientId = authClientId()
  const redirectUri = authRedirectUri()

  if (!(domain && clientId && redirectUri)) return null

  return (
    <Auth0Provider
      domain={domain}
      clientId={clientId}
      authorizationParams={{ redirect_uri: redirectUri, audience: authAudience() }}
      cacheLocation={embedded ? "localstorage" : "memory"}
      useRefreshTokens={embedded}
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  )
}

export const AuthProvider = ({ children, onRedirectCallback }) =>
  isKeycloak() ? (
    <KeycloakAuthProvider onRedirect={(state) => onRedirectCallback?.(state)}>{children}</KeycloakAuthProvider>
  ) : (
    <Auth0AuthProvider onRedirectCallback={onRedirectCallback}>{children}</Auth0AuthProvider>
  )
